import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, message, Empty, Spin } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import {
  getFilteredOpportunities,
  getFilteredOpportunityLocation,
} from '../../../services/api';
import { FilteredOpportunity, LLMRating, FilterReason, FilterReasonCounts } from '../../../types';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import FilteredOpportunityDrawer from '../../../components/drawers/FilteredOpportunityDrawer';
import { useListPageState } from '../../../hooks/useListPageState';
import css from './FilteredTab.module.scss';

interface FilteredTabProps {
  searchId?: string;
}

const ratingConfig: Record<LLMRating, { stars: string; color: string; label: string }> = {
  perfect_fit: { stars: '\u2605\u2605\u2605\u2605\u2605', color: '#52c41a', label: 'Perfect Fit' },
  strong_alignment: { stars: '\u2605\u2605\u2605\u2605\u2606', color: '#73d13d', label: 'Strong' },
  potential_candidate: { stars: '\u2605\u2605\u2605\u2606\u2606', color: '#faad14', label: 'Potential' },
  partial_fit: { stars: '\u2605\u2605\u2606\u2606\u2606', color: '#fa8c16', label: 'Partial' },
  misalignment: { stars: '\u2605\u2606\u2606\u2606\u2606', color: '#ff4d4f', label: 'Weak' },
};

const filterReasonConfig: Record<FilterReason, { label: string; color: string }> = {
  low_similarity: { label: 'Low Similarity', color: 'blue' },
  low_llm_rating: { label: 'Low Rating', color: 'orange' },
  no_contacts: { label: 'No Contacts', color: 'red' },
  manual_intervention: { label: 'Manual Review', color: 'purple' },
  // Honest copy (design §13): the user's own minimum-match setting did this,
  // not our quality bar.
  below_user_threshold: { label: 'Filtered by your minimum match setting', color: 'geekblue' },
  not_eligible: { label: 'Not eligible', color: 'volcano' },
};

const FilteredTab: React.FC<FilteredTabProps> = ({ searchId }) => {
  const [filteredOpportunities, setFilteredOpportunities] = useState<FilteredOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const { selectedId, page, pageSize, setPage, openDrawer, closeDrawer } =
    useListPageState({ basePath: '/opportunities/filtered' });
  const navigate = useNavigate();
  const [byReason, setByReason] = useState<FilterReasonCounts>({
    lowSimilarity: 0,
    lowLlmRating: 0,
    noContacts: 0,
    manualIntervention: 0,
    belowUserThreshold: 0,
    notEligible: 0,
  });

  const selectedOpportunity = useMemo<FilteredOpportunity | null>(
    () => (selectedId ? filteredOpportunities.find(f => f.id === selectedId) ?? null : null),
    [filteredOpportunities, selectedId]
  );
  const drawerOpen = Boolean(selectedOpportunity);

  const fetchFilteredOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFilteredOpportunities({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        searchId,
        sort_field: 'created_at',
        sort_method: 'desc',
      });
      setFilteredOpportunities(response.filteredOpportunities);
      setTotal(response.total);
      setByReason(response.byReason);
    } catch (error) {
      console.error('Fetch filtered opportunities error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchId]);

  useEffect(() => {
    fetchFilteredOpportunities();
  }, [fetchFilteredOpportunities]);

  useEffect(() => {
    if (page !== 1) setPage(1, undefined, { replace: true });
  }, [searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtered opportunities have their own ID space — no cross-bucket redirect.
  useEffect(() => {
    if (!selectedId) return;
    if (loading) return;
    if (filteredOpportunities.some(f => f.id === selectedId)) return;

    let cancelled = false;
    (async () => {
      try {
        const loc = await getFilteredOpportunityLocation(selectedId, pageSize);
        if (cancelled) return;
        if (loc.page !== page) {
          setPage(loc.page, undefined, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;
        message.error('Filtered opportunity not found');
        navigate('/opportunities/filtered', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // The pagination handles are read but not listed, on purpose. `setPage`,
    // `redirectToBucket` and `navigate` are all rebuilt whenever the URL
    // changes — useListPageState keys them on `searchParams` — and changing the
    // URL is exactly what this effect does. Listing them, or `page`/`pageSize`,
    // re-enters the effect from its own `setPage` before the refetch it
    // triggers has landed, firing a second getFilteredOpportunityLocation for an id
    // that is already being located. `loading`
    // and the list are the honest triggers: they are what tells us whether the
    // id is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, filteredOpportunities, loading]);

  const handleViewDetails = (record: FilteredOpportunity) => {
    openDrawer(record.id);
  };

  const handleDrawerClose = () => {
    closeDrawer();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatSimilarityScore = (score: number): string => {
    return `${Math.round(score * 100)}%`;
  };

  const columns = [
    {
      title: 'Rating',
      key: 'rating',
      width: 120,
      render: (_: unknown, record: FilteredOpportunity) => {
        if (!record.llmRating) {
          return <span className={css.noRating}>{'\u2014'}</span>;
        }
        const config = ratingConfig[record.llmRating];
        return (
          <span style={{ color: config.color, letterSpacing: '2px' }} title={config.label}>
            {config.stars}
          </span>
        );
      },
    },
    {
      title: 'Company',
      key: 'company',
      width: 250,
      render: (_: unknown, record: FilteredOpportunity) => (
        // TODO(a11y): make this a real <button type="button">, the way
        // OpportunitiesTab's title cell already is. Deferred rather than done
        // here because the swap is not a one-liner: this file's `.titleLink`
        // styles an <a>, so it carries none of the UA-button reset
        // (appearance/background/border/padding/font/text-align/white-space/
        // max-width) that OpportunitiesTab.module.scss needed, and changing the
        // element without that CSS would visibly alter the desktop table —
        // which CLAUDE.md pins as the frozen reference. Until then the row's
        // "View" Button calls the same handleViewDetails and is
        // keyboard-reachable; unlike OpportunitiesTab this table keeps that
        // column at every viewport, so nothing is stranded behind a pointer.
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a onClick={() => handleViewDetails(record)} className={css.titleLink}>
          {record.company.name}
        </a>
      ),
    },
    {
      title: 'Job Title',
      key: 'jobTitle',
      width: 420,
      render: (_: unknown, record: FilteredOpportunity) => (
        <span>{record.summary?.jobTitle || '\u2014'}</span>
      ),
    },
    {
      title: 'Search',
      dataIndex: ['search', 'searchRole'],
      key: 'search',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: ['search', 'searchLocation'],
      key: 'location',
      width: 240,
    },
    {
      title: 'Filter Reason',
      key: 'filterReason',
      render: (_: unknown, record: FilteredOpportunity) => {
        const config = filterReasonConfig[record.reason];
        return (
          <Tag color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Similarity',
      key: 'similarity',
      width: 100,
      render: (_: unknown, record: FilteredOpportunity) => (
        <span>{formatSimilarityScore(record.similarityScore)}</span>
      ),
    },
    {
      title: 'Date',
      key: 'date',
      width: 130,
      render: (_: unknown, record: FilteredOpportunity) => (
        <span>{formatDate(record.createdAt)}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: unknown, record: FilteredOpportunity) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading && filteredOpportunities.length === 0) {
    return (
      <div className={css.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!loading && filteredOpportunities.length === 0) {
    return (
      <div className={css.emptyContainer}>
        <Empty
          description="No filtered opportunities"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <p>Low-quality matches that were automatically filtered out will appear here.</p>
        </Empty>
      </div>
    );
  }

  return (
    <div className={css.filteredTab}>
      <div className={css.summaryStats}>
        <span className={css.summaryLabel}>Filtered by:</span>
        <Tag color="blue">Low Similarity ({byReason.lowSimilarity})</Tag>
        <Tag color="orange">Low Rating ({byReason.lowLlmRating})</Tag>
        <Tag color="red">No Contacts ({byReason.noContacts})</Tag>
        <Tag color="purple">Manual ({byReason.manualIntervention})</Tag>
        {(byReason.belowUserThreshold ?? 0) > 0 && (
          <Tag color="geekblue">Your minimum match setting ({byReason.belowUserThreshold})</Tag>
        )}
        {(byReason.notEligible ?? 0) > 0 && (
          <Tag color="volcano">Not eligible ({byReason.notEligible})</Tag>
        )}
      </div>
      <Table
        dataSource={filteredOpportunities}
        columns={columns}
        rowKey="id"
        loading={loading}
        onRow={(record) => ({
          onClick: () => handleViewDetails(record),
          style: { cursor: 'pointer' },
        })}
        onChange={(pagination) => {
          setPage(pagination.current ?? 1, pagination.pageSize ?? 10);
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `${total} filtered`,
        }}
      />

      <FilteredOpportunityDrawer
        opportunity={selectedOpportunity}
        open={drawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  );
};

export default FilteredTab;
